import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import './Profile.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';

function Profile() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userProfile, session, signOut, refreshProfile } = useAuth();
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [taskHistory, setTaskHistory] = useState([]);
  const [taskTotal, setTaskTotal] = useState(0);
  const [taskPage, setTaskPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [recharging, setRecharging] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const paymentHandled = useRef(false);

  useEffect(() => {
    if (paymentHandled.current) return;
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      const sessionId = searchParams.get('session_id');
      if (!session?.access_token || !sessionId) return;
      paymentHandled.current = true;
      const verifyPayment = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/payment/verify`, {
            params: { session_id: sessionId },
            headers: { 'Authorization': `Bearer ${session.access_token}` }
          });
          if (response.data?.data?.order_status === 'completed') {
            setPaymentMessage({ type: 'success', text: '充值成功！余额已更新' });
            await refreshProfile();
          } else {
            setPaymentMessage({ type: 'info', text: '支付正在确认，请稍后刷新余额' });
          }
        } catch (error) {
          setPaymentMessage({ type: 'info', text: error.response?.data?.error || '支付正在确认，请稍后刷新余额' });
        } finally {
          window.history.replaceState({}, '', '/profile');
        }
      };
      verifyPayment();
    } else if (paymentStatus === 'cancelled') {
      paymentHandled.current = true;
      setPaymentMessage({ type: 'info', text: '支付已取消' });
      window.history.replaceState({}, '', '/profile');
    }
  }, [searchParams, refreshProfile, session]);

  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(taskTotal / PAGE_SIZE);

  useEffect(() => {
    const fetchTasks = async () => {
      if (!session?.access_token) { setLoading(false); return; }
      try {
        const offset = (taskPage - 1) * PAGE_SIZE;
        const response = await axios.get(`${API_BASE_URL}/user/tasks?offset=${offset}&limit=${PAGE_SIZE}`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
        if (response.data.success) {
          setTaskHistory(response.data.data.tasks || []);
          setTaskTotal(response.data.data.total || 0);
        }
      } catch (error) { console.error('获取任务历史失败:', error); }
      finally { setLoading(false); }
    };
    fetchTasks();
  }, [session, taskPage]);

  const handleRecharge = async () => {
    const amount = parseFloat(rechargeAmount);
    if (!amount || amount < 5) { alert('充值金额最低 ¥5'); return; }
    setRecharging(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/payment/create-session`,
        { amount },
        { headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' } }
      );
      if (response.data.success && response.data.data.url) {
        window.location.href = response.data.data.url;
      } else { throw new Error('获取支付链接失败'); }
    } catch (error) {
      console.error('创建支付失败:', error);
      alert(error.response?.data?.error || '创建支付失败，请重试');
      setRecharging(false);
    }
  };

  const handleDownloadSRT = async (task) => {
    if (!task.subtitle_path || !session?.access_token) {
      alert('字幕文件不可用');
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/subtitle/tasks/${task.id}/download`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const anchor = document.createElement('a');
      anchor.href = response.data.data.url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.click();
    } catch (error) {
      alert(error.response?.data?.error || '字幕下载失败，请稍后重试');
    }
  };

  const handleLogout = async () => {
    try { await signOut(); navigate('/'); }
    catch (error) { console.error('登出失败:', error); }
  };

  const { theme, toggleTheme } = useTheme();
  const userBalance = userProfile?.balance || 0;
  const frozenBalance = userProfile?.frozen_balance || 0;

  return (
    <div className="pf-page">
      {/* 支付状态提示 */}
      {paymentMessage && (
        <div className={`pf-toast pf-toast--${paymentMessage.type}`}>
          <span>{paymentMessage.text}</span>
          <button onClick={() => setPaymentMessage(null)}>×</button>
        </div>
      )}

      {/* 顶栏 */}
      <header className="pf-header">
        <div className="pf-header-left">
          <button onClick={() => navigate('/')} className="pf-nav-btn">← 首页</button>
          <span className="pf-path">
            <span className="pf-path-sep">/</span> 个人中心
          </span>
        </div>
        <div className="pf-header-right">
          <div className="theme-toggle" onClick={toggleTheme} title="切换主题">
            <span className={`theme-toggle-label theme-toggle-label--light ${theme === 'light' ? 'theme-toggle-label--active' : ''}`}>浅色</span>
            <span className={`theme-toggle-label theme-toggle-label--dark ${theme === 'dark' ? 'theme-toggle-label--active' : ''}`}>深色</span>
            <span className={`theme-toggle-indicator ${theme === 'dark' ? 'theme-toggle-indicator--dark' : ''}`} />
          </div>
          <button onClick={() => navigate('/workspace')} className="pf-nav-btn">工作台</button>
          <button onClick={handleLogout} className="pf-nav-btn pf-nav-btn--danger">退出</button>
        </div>
      </header>

      <main className="pf-main">
        {/* 用户信息 */}
        <div className="pf-terminal">
          <div className="pf-terminal-bar">
            <span className="pf-dot pf-dot--red" />
            <span className="pf-dot pf-dot--yellow" />
            <span className="pf-dot pf-dot--green" />
            <span className="pf-terminal-title">用户信息</span>
          </div>
          <div className="pf-terminal-body">
            <div className="pf-info-line">
              <span className="pf-info-key">邮箱</span>
              <span className="pf-info-val">{user?.email || '未登录'}</span>
            </div>
            <div className="pf-info-line">
              <span className="pf-info-key">用户ID</span>
              <span className="pf-info-val">{user?.id?.substring(0, 8).toUpperCase() || 'N/A'}</span>
            </div>
            <div className="pf-info-line">
              <span className="pf-info-key">可用余额</span>
              <span className="pf-info-val pf-info-val--green">¥{userBalance.toFixed(2)}</span>
            </div>
            <div className="pf-info-line">
              <span className="pf-info-key">冻结金额</span>
              <span className="pf-info-val">¥{frozenBalance.toFixed(2)}</span>
            </div>
            <div className="pf-info-action">
              <button onClick={() => setShowRechargeModal(true)} className="pf-recharge-btn">+ 充值</button>
            </div>
          </div>
        </div>

        {/* 历史任务 */}
        <div className="pf-terminal">
          <div className="pf-terminal-bar">
            <span className="pf-dot pf-dot--red" />
            <span className="pf-dot pf-dot--yellow" />
            <span className="pf-dot pf-dot--green" />
            <span className="pf-terminal-title">历史任务</span>
            <span className="pf-terminal-count">{taskHistory.length}</span>
          </div>
          {loading ? (
            <div className="pf-table-empty">
              <span className="pf-prompt">$</span> 加载中...
            </div>
          ) : taskHistory.length === 0 ? (
            <div className="pf-table-empty">
              <span className="pf-prompt">$</span> 暂无任务记录
            </div>
          ) : (
            <>
            <div className="pf-table-wrap">
              <div className="pf-table-header">
                <span className="pf-th">任务</span>
                <span className="pf-th">状态</span>
                <span className="pf-th pf-th--cost">扣费</span>
                <span className="pf-th pf-th--time">时间</span>
                <span className="pf-th pf-th--action">操作</span>
              </div>
              <div className="pf-table-body">
                {taskHistory.map((task, idx) => (
                  <div key={task.id} className="pf-table-row">
                    <div className="pf-td pf-td--name">
                      <span className="pf-row-num">{idx + 1}</span>
                      <span>{task.id.substring(0, 8).toUpperCase()}</span>
                    </div>
                    <div className="pf-td">
                      <span className={`pf-status-tag pf-status--${task.status}`}>
                        {task.status === 'completed' ? '已完成' :
                         task.status === 'processing' ? '处理中' :
                         task.status === 'failed' ? '失败' : '等待中'}
                      </span>
                    </div>
                    <div className="pf-td pf-td--cost">¥{(task.cost || 0).toFixed(2)}</div>
                    <div className="pf-td pf-td--time">
                      {new Date(task.created_at).toLocaleString('zh-CN', {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                    <div className="pf-td pf-td--action">
                      {task.status === 'completed' ? (
                        <button onClick={() => handleDownloadSRT(task)} className="pf-nav-btn">↓ SRT</button>
                      ) : (
                        <span className="pf-td--placeholder">-</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {totalPages > 1 && (
              <div className="pf-pagination">
                <button
                  onClick={() => setTaskPage(p => p - 1)}
                  disabled={taskPage <= 1}
                  className="pf-page-btn"
                >← 上一页</button>
                <span className="pf-page-info">{taskPage} / {totalPages}</span>
                <button
                  onClick={() => setTaskPage(p => p + 1)}
                  disabled={taskPage >= totalPages}
                  className="pf-page-btn"
                >下一页 →</button>
              </div>
            )}
            </>
          )}
        </div>
      </main>

      {/* 充值弹窗 */}
      {showRechargeModal && (
        <div className="pf-modal-overlay" onClick={() => setShowRechargeModal(false)}>
          <div className="pf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pf-modal-bar">
              <span className="pf-dot pf-dot--red" />
              <span className="pf-dot pf-dot--yellow" />
              <span className="pf-dot pf-dot--green" />
              <span className="pf-terminal-title">充值</span>
              <button onClick={() => setShowRechargeModal(false)} className="pf-modal-close">×</button>
            </div>
            <div className="pf-modal-body">
              <div className="pf-cmd-line">
                <span className="pf-prompt">$</span>
                <span className="pf-cmd-text">选择金额</span>
              </div>
              <div className="pf-preset-grid">
                {['50', '100', '200', '500'].map(amt => (
                  <button
                    key={amt}
                    onClick={() => setRechargeAmount(amt)}
                    className={`pf-preset-btn ${rechargeAmount === amt ? 'pf-preset-btn--active' : ''}`}
                  >¥{amt}</button>
                ))}
              </div>
              <div className="pf-cmd-line">
                <span className="pf-prompt">$</span>
                <span className="pf-cmd-text">自定义金额（最低 ¥5）</span>
              </div>
              <div className="pf-amount-input">
                <span className="pf-amount-prefix">¥</span>
                <input
                  type="number"
                  value={rechargeAmount}
                  onChange={(e) => setRechargeAmount(e.target.value)}
                  placeholder="输入充值金额"
                  min="5"
                  disabled={recharging}
                />
              </div>
              <div className="pf-payment-hint">支持微信支付、信用卡</div>
              <button
                onClick={handleRecharge}
                disabled={!rechargeAmount || parseFloat(rechargeAmount) < 5 || recharging}
                className="pf-pay-btn"
              >
                {recharging ? '正在跳转...' : '前往支付'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
